import { useCallback, useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  AutomationProjections,
  AutomationTarget,
  Queue,
} from '@rossum/api-client/queues';
import { ConfirmationDialog } from '@rossum/components/confirmation-dialog/ConfirmationDialog';
import { EmptyStateRadar } from '@rossum/ui';
import { IconBolt, IconBoltOff } from '@rossum/ui/icons/tabler';
import {
  Button,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { SchemaWithEtag } from '../../../../business/schema/useSchema';
import { boldText, linebreak } from '../../../../lib/formaterValues';
import { SchemaFieldWithPathNonNullableField } from '../../../schemas/getFlattenSchemaFields';
import { AutomationLevelOptions } from '../core/components/AutomationLevelOptions';
import { StoredOptimizationAlert } from '../core/components/StoredOptimizationAlert';
import { useAutomationFlags } from '../core/hooks/useAutomationFlags';
import { OptimizationData } from '../core/types';
import { convertBaselineToOptimizationData } from '../core/utils';
import { OptimizeAutomationChart } from './components/OptimizeAutomationChart';
import {
  OPTIMIZATION_FORM_ID,
  OptimizeAutomationData,
} from './components/OptimizeAutomationData';
import { ReadOnlyAssistantAlert } from './components/ReadOnlyAssistantAlert';
import { useGetAutomationProjections } from './hooks/useGetAutomationProjections';
import { useGetCustomProjections } from './hooks/useGetCustomProjections';
import { useOptimizationForm } from './hooks/useOptimisationForm';
import { useSaveOptimizationHandlers } from './hooks/useSaveOptimizationHandlers';
import { CustomPrecision, ExcludeBlockers } from './types';
import { precisionSchema } from './utils';

type OptimizationAssistantProps = {
  queue: Queue;
  schemaMap: Record<string, SchemaFieldWithPathNonNullableField>;
  queueSchema: SchemaWithEtag | undefined;
  savedAutomationTargets: AutomationTarget[] | undefined;
  onClose: () => void;
  onSwitchBack: () => void;
  readOnly: boolean;
};

export const OptimizationAssistant = ({
  onClose,
  onSwitchBack,
  readOnly,
  queue,
  schemaMap,
  queueSchema,
  savedAutomationTargets,
}: OptimizationAssistantProps) => {
  const intl = useIntl();
  const [targetState, setTargetState] = useState<OptimizationData | null>(null);
  const [customProjections, setCustomProjections] =
    useState<AutomationProjections | null>(null);
  const [excludeBlockers, setExcludeBlockers] = useState<ExcludeBlockers>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isQualitySpotCheckDialogOpen, setIsQualitySpotCheckDialogOpen] =
    useState(false);

  const [automationLevel, setAutomationLevel] = useState(queue.automationLevel);

  const isLocalStateConfident = automationLevel === 'confident';
  const { queueLevelFlags } = useAutomationFlags();

  const isQueueAutomationConfident =
    queueLevelFlags.isAutomationConfident(queue);
  const isUsingAssistant = queueLevelFlags.isUsingAssistant(queue);
  const isQualitySpotCheckEnabled = queue.qualitySpotCheckPercentage > 0;

  const formMethods = useOptimizationForm();

  const {
    reset,
    handleSubmit,
    clearErrors,
    getValues,
    formState: { dirtyFields },
  } = formMethods;

  // TODO the native isDirty prop of RHF seems to be unreliable in that even after resetting the form state to its initial value it still remains true
  // as a workaround for this prototype we're instead relying on dirtyFields here.
  const isDirty = Object.keys(dirtyFields).length > 0;

  const {
    mutate: getCustomProjections,
    isLoading: isGettingCustomProjections,
  } = useGetCustomProjections();

  const { data: automationProjections, isFetching: isLoadingProjections } =
    useGetAutomationProjections({
      queueId: queue.id,
      query: { excludeBlockers },
      enabled: isLocalStateConfident,
      payload: {
        fields: [],
      },
    });

  const currentState = useMemo<OptimizationData | null>(() => {
    if (!automationProjections) return null;

    return convertBaselineToOptimizationData(automationProjections.baseline);
  }, [automationProjections]);

  const handleAutomationSaveOnSuccess = useCallback(() => {
    // Drop the picked target so the save button disables; the chart will
    // re-anchor to the refetched baseline as the new current state.
    setTargetState(null);
    setCustomProjections(null);
  }, []);

  const {
    isInvalidating,
    isSaving,
    handleSave,
    handleSwitchBack,
    isSavingDisabled,
  } = useSaveOptimizationHandlers({
    automationLevel,
    queue,
    targetState,
    currentState,
    getUserDefinedPrecisionTargets: getValues,
    savedAutomationTargets,
    queueSchema,
    onSaveSuccess: handleAutomationSaveOnSuccess,
  });

  const onSubmit = (data: CustomPrecision) => {
    // convert form data into payload format
    const fields = R.pipe(
      data,
      R.entries(),
      R.flatMap(([schemaId, precision]) => {
        const parsed = precisionSchema.safeParse(precision);

        return parsed.success && parsed.data
          ? [[schemaId, parsed.data] as const]
          : [];
      }),
      R.map(([schemaId, precision]) => {
        const errorRateLimit = precision === 0 ? 0 : 1 - precision / 100;
        return {
          schemaId,
          errorRateLimit,
        };
      })
    );

    getCustomProjections(
      {
        payload: {
          fields,
        },
        queueId: queue.id,
        query: { excludeBlockers },
      },
      {
        onSuccess: response => {
          setTargetState(null);
          setCustomProjections(response);
          // clearing the isDirty state and errors
          clearErrors();
          reset(getValues());
        },
      }
    );
  };

  const handleSetFilters = (type: ExcludeBlockers[number]) => {
    setExcludeBlockers(prev =>
      prev.includes(type) ? prev.filter(f => f !== type) : [...prev, type]
    );
    setTargetState(null);
    reset();
  };

  const handleClose = () => {
    onClose();
    clearErrors();
  };

  const isLoading = isGettingCustomProjections || isLoadingProjections;

  // targetState can only be defined when automation level is confident. set it to null if user changes level
  useEffect(() => {
    if (automationLevel !== 'confident' && targetState !== null)
      setTargetState(null);
  }, [automationLevel, targetState]);

  return (
    <Stack sx={{ overflow: 'hidden', flexGrow: 1 }}>
      <LinearProgress
        variant="indeterminate"
        sx={{ visibility: isSaving || isInvalidating ? 'visible' : 'hidden' }}
      />

      <Stack
        sx={{
          overflow: 'hidden',
          p: 3,
          flexGrow: 1,
        }}
        spacing={3}
        useFlexGap
      >
        <Stack sx={{ overflow: 'hidden', flexGrow: 1 }} spacing={3} useFlexGap>
          {readOnly ? (
            // a non-confident queue is not automated by the assistant at all,
            // so there is nothing to explain
            isLocalStateConfident ? (
              <ReadOnlyAssistantAlert />
            ) : null
          ) : isUsingAssistant && isQueueAutomationConfident ? (
            <StoredOptimizationAlert
              savedAutomationTargets={savedAutomationTargets}
            />
          ) : null}

          <AutomationLevelOptions
            automationLevel={automationLevel}
            onChange={value => {
              setAutomationLevel(value);
            }}
          />

          {isLocalStateConfident ? (
            <Stack
              sx={{ overflow: 'auto', flexGrow: 1 }}
              spacing={2}
              useFlexGap
            >
              <FormProvider {...formMethods}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  id={OPTIMIZATION_FORM_ID}
                >
                  {readOnly ? (
                    <Stack sx={{ pb: 1 }}>
                      <Typography variant="h5">
                        {intl.formatMessage({
                          id: 'containers.settings.queues.queue.automation.readOnly.title',
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage({
                          id: 'containers.settings.queues.queue.automation.readOnly.description',
                        })}
                      </Typography>
                    </Stack>
                  ) : (
                    <OptimizeAutomationChart
                      currentState={currentState}
                      automationProjections={
                        customProjections ?? automationProjections
                      }
                      isLoading={isLoading}
                      setTargetState={setTargetState}
                      targetState={targetState}
                      isDirty={isDirty}
                    />
                  )}

                  <OptimizeAutomationData
                    isLoading={isLoading}
                    targetState={targetState}
                    currentState={currentState}
                    schemaMap={schemaMap}
                    readOnly={readOnly}
                  />
                </form>
              </FormProvider>
              {/* the filters only exist to refine the projections a target is picked from */}
              {readOnly ? null : (
                <Stack spacing={1} useFlexGap>
                  <Typography variant="h6">
                    {intl.formatMessage({
                      id: 'containers.settings.queues.queue.automation.advancedSettings.title',
                    })}
                  </Typography>
                  <Stack>
                    <FormControlLabel
                      disabled={isLoading}
                      control={
                        <Switch
                          checked={excludeBlockers.includes('error_message')}
                          onChange={() => handleSetFilters('error_message')}
                        />
                      }
                      // FormControlLabel has a default negative margin ¯\_(ツ)_/¯
                      sx={{ m: 0 }}
                      label={intl.formatMessage({
                        id: 'containers.settings.queues.queue.automation.advancedSettings.filters.errorMessageBlockers',
                      })}
                    />
                    <FormControlLabel
                      disabled={isLoading}
                      control={
                        <Switch
                          checked={excludeBlockers.includes('extension')}
                          onChange={() => handleSetFilters('extension')}
                        />
                      }
                      sx={{ m: 0 }}
                      label={intl.formatMessage({
                        id: 'containers.settings.queues.queue.automation.advancedSettings.filters.extensionBlockers',
                      })}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          ) : (
            <EmptyStateRadar
              title={intl.formatMessage(
                {
                  id: 'containers.settings.queues.queue.automation.optimizeDrawer.nonConfident.title',
                },
                {
                  boldText,
                  automationLevel: intl.formatMessage({
                    id: `containers.settings.automation.documentAutomation.${queue.automationLevel}.title`,
                  }),
                }
              )}
              description={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.optimizeDrawer.nonConfident.description',
              })}
              iconProps={{
                Icon:
                  queue.automationLevel === 'never' ? IconBoltOff : IconBolt,
                iconSize: 32,
              }}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap>
          <Stack sx={{ flexGrow: 1 }}>
            {isUsingAssistant && isQueueAutomationConfident ? (
              <Button
                color="secondary"
                onClick={() => setIsConfirmDialogOpen(true)}
                sx={{ alignSelf: 'flex-start' }}
                disabled={isSaving || isInvalidating}
              >
                {intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.optimizeDrawer.switchBack',
                })}
              </Button>
            ) : null}
          </Stack>

          <Button variant="outlined" color="secondary" onClick={handleClose}>
            {intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.optimizeDrawer.cancel',
            })}
          </Button>
          <Tooltip
            title={
              isSavingDisabled || isDirty
                ? intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.optimizeDrawer.saveTooltip',
                  })
                : ''
            }
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                onClick={() => {
                  // the assistant needs QSC data to keep the saved target accurate,
                  // so it has to be enabled before the target gets saved
                  if (targetState && !isQualitySpotCheckEnabled) {
                    setIsQualitySpotCheckDialogOpen(true);
                  } else handleSave();
                }}
                disabled={
                  isSavingDisabled || isDirty || isSaving || isInvalidating
                }
              >
                {intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.optimizeDrawer.save',
                })}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        title={intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimizeDrawer.switchBack.confirmDialog.title',
        })}
        description={
          <Typography variant="body2">
            {intl.formatMessage(
              {
                id: 'containers.settings.queues.queue.automation.optimizeDrawer.switchBack.confirmDialog.description',
              },
              { linebreak, boldText }
            )}
          </Typography>
        }
        onConfirm={() => {
          handleSwitchBack(() => {
            setIsConfirmDialogOpen(false);
            onSwitchBack();
          });
        }}
        onCancel={() => setIsConfirmDialogOpen(false)}
        isLoading={isSaving || isInvalidating}
        confirmActionText={intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.optimizeDrawer.switchBack.confirmDialog.confirmButton',
        })}
      />

      <ConfirmationDialog
        open={isQualitySpotCheckDialogOpen}
        title={intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.legacySettings.qualitySpotCheckDialog.title',
        })}
        description={intl.formatMessage(
          {
            id: 'containers.settings.queues.queue.automation.legacySettings.qualitySpotCheckDialog.description',
          },
          { linebreak }
        )}
        onConfirm={() => {
          setIsQualitySpotCheckDialogOpen(false);
          handleSave({ enableQualitySpotCheck: true });
        }}
        onCancel={() => setIsQualitySpotCheckDialogOpen(false)}
        isLoading={isSaving || isInvalidating}
        confirmActionText={intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.legacySettings.qualitySpotCheckDialog.confirmActionText',
        })}
      />
    </Stack>
  );
};
